"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Field } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { useSearchCityQuery } from "@/lib/api/common-api";
import { cn } from "@/lib/utils";
import { Check, LoaderCircle, MapPin } from "lucide-react";
import { useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IHolidaySearchSchema } from "../../schema/holiday-search.schema";

interface SelectHolidayCityProps {
  form: UseFormReturn<IHolidaySearchSchema>;
  popoverModal?: boolean;
}

type THolidayCityOption = {
  id: number;
  name: string;
  country_name?: string;
  lat?: number | null;
  lng?: number | null;
};

export default function SelectHolidayCity({
  form,
  popoverModal = false,
}: SelectHolidayCityProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data, isLoading } = useSearchCityQuery({
    name: debouncedSearchTerm,
    limit: 20,
    skip: 0,
  });
  const cityOptions = (data?.data ?? []) as unknown as THolidayCityOption[];

  return (
    <Controller
      control={form.control}
      name="city"
      render={({ field, fieldState }) => {
        const value = field.value;
        const hasError = fieldState.invalid || Boolean(fieldState.error);

        return (
          <Field data-invalid={hasError}>
            <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  role="combobox"
                  aria-expanded={open}
                  aria-invalid={hasError}
                  className={cn(
                    "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40",
                    !value?.city && "text-muted-foreground",
                    hasError && "border-destructive",
                  )}
                >
                  {value?.city ? (
                    <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
                      <span className="text-base font-bold tracking-tight text-foreground sm:text-[1.05rem]">
                        {value.city.slice(0, 3).toUpperCase()}
                      </span>
                      <span className="h-9 w-px bg-border" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-foreground/85">
                          {value.city}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {value.country_name || "Holiday destination"}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 text-left">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                        Destination
                      </span>
                      <span className="block truncate text-sm text-muted-foreground">
                        All destinations
                      </span>
                    </span>
                  )}
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-fit p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name, city or country..."
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                    className="h-9"
                  />
                  {isLoading ? (
                    <div className="py-3 flex justify-end px-3">
                      <LoaderCircle className="animate-spin" />
                    </div>
                  ) : (
                    <CommandList className="no-scrollbar">
                      <CommandEmpty>No city found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          className={cn(
                            "cursor-pointer w-full border-b py-2 px-2 truncate",
                            !value?.city_id
                              ? "bg-primary text-primary-foreground"
                              : "",
                          )}
                          value="all-destination"
                          onSelect={() => {
                            setOpen(false);
                            form.resetField("city", {
                              defaultValue: null,
                            });
                          }}
                        >
                          <div className="w-full truncate flex items-center justify-between">
                            <span className="font-medium">
                              All Destinations
                            </span>
                            <Check
                              className={cn(
                                "h-4 w-4",
                                !value?.city_id ? "opacity-100" : "opacity-0",
                              )}
                            />
                          </div>
                        </CommandItem>
                        {cityOptions.map((item) => {
                          const isSelected = value?.city_id === item.id;

                          return (
                            <CommandItem
                              key={item.id}
                              className={cn(
                                "cursor-pointer w-full border-b py-2 px-2 truncate",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "",
                              )}
                              value={String(item.id)}
                              onSelect={() => {
                                setOpen(false);
                                field.onChange({
                                  city_id: item.id,
                                  city: item.name,
                                  country_name: item.country_name,
                                  lat: item.lat ?? undefined,
                                  lng: item.lng ?? undefined,
                                });
                              }}
                            >
                              <div className="w-full truncate flex items-center justify-between">
                                <div>
                                  <div className="flex flex-col truncate">
                                    <span
                                      className={cn(
                                        "font-medium",
                                        isSelected
                                          ? "text-white"
                                          : "text-primary",
                                      )}
                                    >
                                      {item.name}
                                    </span>
                                  </div>

                                  <div className="flex justify-between items-center">
                                    <span className="text-xs truncate flex items-center gap-1">
                                      <MapPin
                                        className={cn(
                                          "size-3 text-primary",
                                          isSelected && "text-white",
                                        )}
                                      />
                                      {item.name}, {item.country_name}
                                    </span>
                                  </div>
                                </div>
                                <Check
                                  className={cn(
                                    "h-4 w-4",
                                    isSelected ? "opacity-100" : "opacity-0",
                                  )}
                                />
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  )}
                </Command>
              </PopoverContent>
            </Popover>
          </Field>
        );
      }}
    />
  );
}
